import React, { useState, useRef, useEffect } from "react";
import { NavLink } from "react-router-dom";

import {
    FiPhone,
    FiMenu,
    FiX,
    FiSun,
    FiMoon,
    FiChevronDown,
} from "react-icons/fi";

import { useTranslation } from "react-i18next";
import i18n from "../i18n";

import Logo from "./Logo";
import WeatherStrip from "./WeatherStrip";

import "./Header.css";

const LANGUAGES = [
    { code: "uz", label: "O'zbek" },
    { code: "en", label: "English" },
    { code: "fr", label: "Français" },
    { code: "ru", label: "Русский" },
];

export default function Header() {
    const { t } = useTranslation();

    const [menuOpen, setMenuOpen] = useState(false);
    const [moreOpen, setMoreOpen] = useState(false);
    const [mobileMoreOpen, setMobileMoreOpen] = useState(false);
    const [isDarkIcon, setIsDarkIcon] = useState(false);

    const menuRef = useRef(null);
    const moreRef = useRef(null);

    const handleLangChange = (e) => {
        const lang = e.target.value;

        i18n.changeLanguage(lang);
        localStorage.setItem("i18nextLng", lang);
    };

    const toggleThemeIcon = () => {
        setIsDarkIcon((prev) => !prev);
    };

    const closeMenu = () => {
        setMenuOpen(false);
        setMobileMoreOpen(false);
        setMoreOpen(false);

        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    useEffect(() => {
        const handleOutsideClick = (e) => {
            if (
                moreRef.current &&
                !moreRef.current.contains(e.target)
            ) {
                setMoreOpen(false);
            }
        };

        if (moreOpen) {
            document.addEventListener(
                "mousedown",
                handleOutsideClick
            );
        }

        return () => {
            document.removeEventListener(
                "mousedown",
                handleOutsideClick
            );
        };
    }, [moreOpen]);

    useEffect(() => {
        const handleMenuOutsideClick = (e) => {
            if (
                menuOpen &&
                menuRef.current &&
                !menuRef.current.contains(e.target)
            ) {
                setMenuOpen(false);
            }
        };

        if (menuOpen) {
            document.addEventListener(
                "mousedown",
                handleMenuOutsideClick
            );

            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "auto";
        }

        return () => {
            document.removeEventListener(
                "mousedown",
                handleMenuOutsideClick
            );

            document.body.style.overflow = "auto";
        };
    }, [menuOpen]);

    return (
        <header className="header">
            <WeatherStrip />

            <div className="header-container">
                {/* LOGO */}
                <NavLink
                    to="/"
                    className="logo"
                    onClick={closeMenu}
                >
                    <Logo />
                </NavLink>

                {/* DESKTOP NAVIGATION */}
                <nav className="nav-desktop">
                    <NavLink
                        to="/"
                        end
                        className="nav-link"
                    >
                        {t("nav.home")}
                    </NavLink>

                    <NavLink
                        to="/uzbekistan"
                        className="nav-link"
                    >
                        {t("nav.uzbekistan")}
                    </NavLink>

                    <NavLink
                        to="/tours"
                        className="nav-link"
                    >
                        {t("nav.tours")}
                    </NavLink>

                    <NavLink
                        to="/experiences"
                        className="nav-link"
                    >
                        {t("nav.experiences")}
                    </NavLink>

                    <NavLink
                        to="/about"
                        className="nav-link"
                    >
                        {t("nav.about")}
                    </NavLink>

                    {/* MORE */}
                    <div
                        className="nav-more"
                        ref={moreRef}
                    >
                        <button
                            type="button"
                            className={`nav-more-button ${moreOpen ? "active" : ""
                                }`}
                            onClick={() =>
                                setMoreOpen((prev) => !prev)
                            }
                        >
                            <span>
                                {t("nav.more", "Ko‘proq")}
                            </span>

                            <FiChevronDown
                                className={
                                    moreOpen ? "rotate" : ""
                                }
                            />
                        </button>

                        <div
                            className={`nav-dropdown ${moreOpen ? "show" : ""
                                }`}
                        >
                            <NavLink
                                to="/services"
                                onClick={() =>
                                    setMoreOpen(false)
                                }
                            >
                                {t("nav.services")}
                            </NavLink>

                            <NavLink
                                to="/cuisine"
                                onClick={() =>
                                    setMoreOpen(false)
                                }
                            >
                                {t("nav.cuisine")}
                            </NavLink>

                            <NavLink
                                to="/gallery"
                                onClick={() =>
                                    setMoreOpen(false)
                                }
                            >
                                {t("nav.gallery")}
                            </NavLink>
                        </div>
                    </div>
                </nav>

                {/* ACTIONS */}
                <div className="header-actions">
                    <NavLink
                        to="/contact"
                        className="contact-btn"
                    >
                        <FiPhone />
                        {t("nav.contact")}
                    </NavLink>

                    <select
                        className="lang-select"
                        onChange={handleLangChange}
                        value={
                            localStorage.getItem(
                                "i18nextLng"
                            ) || "uz"
                        }
                    >
                        {LANGUAGES.map((language) => (
                            <option
                                key={language.code}
                                value={language.code}
                            >
                                {language.label}
                            </option>
                        ))}
                    </select>

                    <button
                        type="button"
                        className="theme-toggle"
                        onClick={toggleThemeIcon}
                        aria-label="Theme"
                    >
                        {isDarkIcon ? (
                            <FiMoon />
                        ) : (
                            <FiSun />
                        )}
                    </button>
                </div>

                {/* MOBILE MENU BUTTON */}
                <button
                    type="button"
                    className="menu-btn"
                    onClick={() => setMenuOpen(true)}
                    aria-label="Open menu"
                >
                    <FiMenu />
                </button>
            </div>

            {/* OVERLAY */}
            <div
                className={`overlay ${menuOpen ? "show" : ""
                    }`}
                onClick={closeMenu}
            />

            {/* MOBILE MENU */}
            <aside
                ref={menuRef}
                className={`mobile-menu ${menuOpen ? "open" : ""
                    }`}
            >
                <button
                    type="button"
                    className="mobile-close"
                    onClick={closeMenu}
                    aria-label="Close menu"
                >
                    <FiX />
                </button>

                <nav className="mobile-inner">
                    {/* PRIMARY LINKS */}
                    <NavLink
                        to="/"
                        end
                        onClick={closeMenu}
                    >
                        {t("nav.home")}
                    </NavLink>

                    <NavLink
                        to="/uzbekistan"
                        onClick={closeMenu}
                    >
                        {t("nav.uzbekistan")}
                    </NavLink>

                    <NavLink
                        to="/tours"
                        onClick={closeMenu}
                    >
                        {t("nav.tours")}
                    </NavLink>

                    <NavLink
                        to="/experiences"
                        onClick={closeMenu}
                    >
                        {t("nav.experiences")}
                    </NavLink>

                    <NavLink
                        to="/about"
                        onClick={closeMenu}
                    >
                        {t("nav.about")}
                    </NavLink>

                    {/* MOBILE MORE */}
                    <div className="mobile-more">
                        <button
                            type="button"
                            className={`mobile-more-button ${mobileMoreOpen
                                    ? "active"
                                    : ""
                                }`}
                            onClick={() =>
                                setMobileMoreOpen(
                                    (prev) => !prev
                                )
                            }
                        >
                            <span>
                                {t("nav.more", "Ko‘proq")}
                            </span>

                            <FiChevronDown
                                className={
                                    mobileMoreOpen
                                        ? "rotate"
                                        : ""
                                }
                            />
                        </button>

                        <div
                            className={`mobile-more-links ${mobileMoreOpen
                                    ? "show"
                                    : ""
                                }`}
                        >
                            <NavLink
                                to="/services"
                                onClick={closeMenu}
                            >
                                {t("nav.services")}
                            </NavLink>

                            <NavLink
                                to="/cuisine"
                                onClick={closeMenu}
                            >
                                {t("nav.cuisine")}
                            </NavLink>

                            <NavLink
                                to="/gallery"
                                onClick={closeMenu}
                            >
                                {t("nav.gallery")}
                            </NavLink>
                        </div>
                    </div>

                    {/* CONTACT */}
                    <NavLink
                        to="/contact"
                        className="contact-btn mobile-cta"
                        onClick={closeMenu}
                    >
                        <FiPhone />
                        {t("nav.contact")}
                    </NavLink>

                    {/* LANGUAGE */}
                    <select
                        className="mobile-select"
                        onChange={handleLangChange}
                        value={
                            localStorage.getItem(
                                "i18nextLng"
                            ) || "uz"
                        }
                    >
                        {LANGUAGES.map((language) => (
                            <option
                                key={language.code}
                                value={language.code}
                            >
                                {language.label}
                            </option>
                        ))}
                    </select>

                    {/* THEME */}
                    <button
                        type="button"
                        className="theme-toggle mobile-theme"
                        onClick={toggleThemeIcon}
                    >
                        {isDarkIcon ? (
                            <FiMoon />
                        ) : (
                            <FiSun />
                        )}
                    </button>
                </nav>
            </aside>
        </header>
    );
}